import { NgFor, NgIf, TitleCasePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbCheckboxModule,
  NbDialogRef,
  NbDialogService,
  NbIconModule,
  NbToastrService,
  NbTooltipModule
} from '@nebular/theme';
import { Actions, Store, ofActionCompleted } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { BatchJobService } from 'src/app/core/services/batch-job.service';
import { OktaRestriction } from 'src/app/core/services/okta-restrictions.service';
import { UtilityService } from 'src/app/core/services/utility.service';
import { CampaignWebSocketService } from 'src/app/core/services/web-socket.service';
import { ViewContentComponent } from 'src/app/features/content/content-table/view-content/view-content.component';
import { ContentState } from 'src/app/features/content/redux/content.state';
import { ViewInsightComponent } from 'src/app/features/insights/insights-table/view-insight/view-insight.component';
import { LoadInsight } from 'src/app/features/insights/redux';
import { SurveysState } from 'src/app/features/surveys/redux';
import { ViewSurveyComponent } from 'src/app/features/surveys/surveys-table/view-survey/view-survey.component';
import { Campaign } from 'src/app/models/campaign';
import { CampaignCondition } from 'src/app/models/helper-classes';
import { Survey } from 'src/app/models/survey';
import { ACTIONS, CAMPAIGN_TYPES, CONFIG_REGEX, STATUSES } from 'src/app/shared/constants/general-constants';
import { CustomValueHandlingPipe } from 'src/app/shared/pipes/custom-value-handling.pipe';
import { FormatTimePipe } from 'src/app/shared/pipes/format-time.pipe';
import { ReplaceUnderscorePipe } from 'src/app/shared/pipes/replace-underscore.pipe';
import { SplitWordUppercasePipe } from 'src/app/shared/pipes/split-word-uppercase.pipe';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { ENVIRONMENT } from 'src/environments/environment';
import { Content } from '../../../../models/content';
import { Insight } from '../../../../models/insight';
import { RouterState } from '../../../auth/redux/router-state';
import { InsightsState } from '../../../insights/redux/insights.state';
import { CampaignState } from '../../redux';
import { ManageCampaign, PromotionSuccess } from '../../redux/campaign.actions';
import { CampaignService } from '../../services/campaign.service';
import { StatsCampaignComponent } from '../stats-campaign/stats-campaign.component';
import { CampaignPromotionComponent } from './campaign-promotion-component/campaign-promotion-component.component';
import { CampaignStatusHandlerComponent } from './campaign-status-handler/campaign-status-handler.component';

@Component({
  selector: 'app-view-campaign',
  templateUrl: './view-campaign.component.html',
  styleUrls: ['./view-campaign.component.css'],
  providers: [CustomValueHandlingPipe],
  imports: [
    NbCardModule,
    NbIconModule,
    NbButtonModule,
    NgIf,
    NbTooltipModule,
    NbCheckboxModule,
    NgFor,
    FormsModule,
    TitleCasePipe,
    CustomValueHandlingPipe,
    FormatTimePipe,
    ReplaceUnderscorePipe,
    SplitWordUppercasePipe
  ]
})
export class ViewCampaignComponent implements OnInit, OnDestroy {
  public campaign$: Observable<Campaign[] | undefined> = inject(Store).select(CampaignState.campaign);
  public activeInsight$: Observable<Insight[] | undefined> = inject(Store).select(InsightsState.insightsActive);
  public inactiveInsight$: Observable<Insight[] | undefined> = inject(Store).select(InsightsState.insightsInactive);
  public content$: Observable<Content[] | undefined> = inject(Store).select(ContentState.content);
  public survey$: Observable<Survey[] | undefined> = inject(Store).select(SurveysState.surveys);
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe = new Subject<void>();

  public campaigns: Campaign[] = [];
  public contents: Content[] = [];
  public insights: Insight[] = [];
  public surveys: Survey[] = [];
  public selectedSurveys: { id: string; name?: string }[] = [];
  public username = '';

  public showPromoterUsername = false;
  public displayPromotionButton = false;
  public disablePromotionButton = false;

  public campaign!: Campaign;
  public condition_all: CampaignCondition[] = [];
  public condition_any: CampaignCondition[] = [];
  public condition_not_all: CampaignCondition[] = [];
  public condition_not_any: CampaignCondition[] = [];
  public time_frame?: number = undefined;
  public show_condition = false;

  public campaignNameValidator = false;
  public frequencyLabel = '-';
  public scheduleLabel = '-';
  public statusLabel = '-';
  public promotedLabel = '-';
  public averageExecutionDuration = 0;
  public payloadRecordTypes: string[] = [];

  public displayManagementButtons = false;
  public valid_campaign = true;
  public campaignTypes = CAMPAIGN_TYPES;

  constructor(
    private utilityService: UtilityService,
    protected ref: NbDialogRef<ViewCampaignComponent>,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
    private customValueHandlingPipe: CustomValueHandlingPipe,
    private action$: Actions,
    private service: CampaignService,
    private batchJobService: BatchJobService,
    private restrictionService: OktaRestriction,
    private store: Store,
    private socketService: CampaignWebSocketService
  ) {}

  public ngOnInit() {
    this.campaign$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.campaigns = data;
        const found = data.find((campaign) => campaign.id === this.campaign.id);
        if (found) {
          this.campaign = found;
        } else {
          this.toastrService.danger('', $localize`Failed to find campaign with ID of ${this.campaign.id}`);
        }
      }
      this.handleUpdate();
    });
    if (this.campaign.responses) {
      this.campaign.responses.forEach((item) => {
        const obj = { name: item.name, id: item.id };
        this.selectedSurveys.push(obj);
      });
    }
    if (this.campaign.condition) {
      this.deserializeCondition();
    }
    this.activeInsight$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.insights = this.insights?.concat(data);
        this.handleUpdate();
      }
    });
    this.inactiveInsight$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.insights = this.insights?.concat(data);
        this.handleUpdate();
      }
    });
    this.content$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.contents = data;
        this.handleUpdate();
      }
    });
    this.survey$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.surveys = data;
      }
    });
    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));

    this.displayManagementButtons = this.restrictionService.manager_permission;
    this.campaignNameValidator = this.regexValidator(this.campaign.name);
    this.store.dispatch(new LoadInsight(this.campaign.trigger_id));

    this.action$.pipe(ofActionCompleted(PromotionSuccess), takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.service.loadCampaignsAmplify();
      }
    });
    this.action$.pipe(ofActionCompleted(ManageCampaign), takeUntil(this.unsubscribe)).subscribe((data) => {
      setTimeout(() => {
        this.campaign = data.action.payload;
        this.updateLabels();
      }, 300);
    });

    this.handleUpdate();
    this.updateLabels();
    if (this.campaign.total_duration && this.campaign.executions) {
      this.averageExecutionDuration = this.campaign.total_duration / this.campaign.executions;
    }
    const content = this.contents.find((element) => element.content_id === this.campaign.content_id);
    if (content) {
      this.valid_campaign = this.service.isValidCampaignType(this.campaign, content);
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public updateLabels() {
    this.statusLabel = this.campaign.enabled ? $localize`Active` : $localize`Inactive`;
    this.promotedLabel = this.campaign.promoted ? $localize`True` : $localize`False`;
    if (this.campaign.schedule_seconds) {
      this.frequencyLabel = this.utilityService.getMatchedFrequencyLabel(this.campaign.schedule_seconds) || '-';
    }
    if (this.campaign.start_date && this.campaign.end_date) {
      this.scheduleLabel =
        this.customValueHandlingPipe.transform(this.campaign.start_date, 'start_date') +
        ' - ' +
        this.customValueHandlingPipe.transform(this.campaign.end_date, 'end_date');
    }
  }

  public handleUpdate() {
    this.showPromoterUsername = this.campaign.promoted ? true : false;

    const foundContent = this.contents.find((element) => element.content_id === this.campaign.content_id);
    if (foundContent && foundContent.payload_records) {
      this.payloadRecordTypes = foundContent.payload_records.map((r) => r.payload_record_type || '');
    }

    if (!ENVIRONMENT.isProduction) {
      const foundInsight = this.insights.find((element) => element.id === Number(this.campaign.trigger_id));

      // Disable promotion button if no insight or no content or the campaign is promoted.
      this.disablePromotionButton =
        foundContent && foundInsight && !this.campaign.promoted && !this.campaign.is_campaign_promotion_in_progress
          ? true
          : false;
      // Do not show button based on user Okta group
      this.displayPromotionButton = this.restrictionService.promotion_permission;
    }
  }

  public regexValidator(value: string | undefined) {
    if (value !== undefined) {
      const regex = new RegExp(String.raw`${CONFIG_REGEX}`, 'i');
      return !regex.test(value);
    }
    return true;
  }

  public viewInsight(event: number | undefined) {
    const found = this.insights.find((element) => element.id === event);
    if (found) {
      this.dialogService.open(ViewInsightComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          insight: found
        }
      });
    } else {
      this.toastrService.show(status, $localize`Insight with this ID does not exist`, {
        status: 'danger'
      });
    }
  }

  public viewContent(event: string | number | undefined) {
    const found = this.contents.find((element) => element.content_id === event);
    if (found) {
      this.dialogService.open(ViewContentComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          content: found
        }
      });
    } else {
      this.toastrService.show(status, $localize`Content with this ID does not exist`, {
        status: 'danger'
      });
    }
  }

  public viewSurvey(event: { id: any }) {
    const found = this.surveys.find((element) => element.survey_id === event.id);
    if (found) {
      this.dialogService.open(ViewSurveyComponent, {
        context: {
          survey: Survey.copy(found) as Survey
        }
      });
    } else {
      this.toastrService.show(status, $localize`Survey with this ID does not exist`, {
        status: 'danger'
      });
    }
  }

  public viewCampaign(event: string) {
    const found = this.campaigns.find((element) => element.id === event);
    if (found) {
      this.dialogService.open(ViewCampaignComponent, {
        context: {
          campaign: Campaign.copy(found) as Campaign
        }
      });
    } else {
      this.toastrService.show(status, $localize`Campaign with this ID does not exist`, {
        status: 'danger'
      });
    }
  }

  public viewStats(event: string) {
    const found = this.campaigns.find((element) => element.id === event);
    if (found) {
      this.dialogService.open(StatsCampaignComponent, {
        context: {
          campaign: Campaign.copy(found) as Campaign
        }
      });
    } else {
      this.toastrService.show(status, $localize`Campaign with this ID does not exist`, {
        status: 'danger'
      });
    }
  }

  public runOnce() {
    if (this.campaign.status === STATUSES.RUNNING) {
      this.toastrService.show(status, $localize`Campaign is already running`, { status: 'danger' });
    } else if (this.valid_campaign) {
      this.dialogService
        .open(NgxGenericModalComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            title: $localize`Starting Campaign: ${this.campaign.id}`,
            message: $localize`Are you sure you want to start this campaign?`
          }
        })
        .onClose.subscribe((data) => {
          if (data) {
            this.socketService.setUp();
            this.batchJobService
              .runCampaign(this.campaign)
              .pipe(takeUntil(this.unsubscribe))
              .subscribe((data) => {
                setTimeout(() => {
                  this.socketService.sendMessage(data.job_id, this.campaign.id);
                }, 2000);
              });
          }
        });
    } else {
      this.dialogService.open(NgxGenericModalComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          title: $localize`Invalid Campaign Configuration`,
          message: $localize`Invalid Configuration: The content attached to this campaign is set up for the Prime App and is not compatible with ${this.campaign.campaign_type} campaign type. 
            Content for Prime is only compatible with Contextual Message, Multi Page Contextual Message, eCommerce, and Map Dirty campaign types.`,
          displayOkButton: true
        }
      });
    }
  }

  public activateCampaign() {
    const associatedInsight = this.insights.find((element) => element.id === Number(this.campaign.trigger_id));
    if (!associatedInsight) {
      this.toastrService.show(status, $localize`No Insight Associated with the Campaign.`, {
        status: 'danger',
        duration: 6000
      });
    }

    if (!this.valid_campaign) {
      this.dialogService.open(NgxGenericModalComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          title: $localize`Invalid Campaign Configuration`,
          message: $localize`Invalid Configuration: The content attached to this campaign is set up for the Prime App and is not compatible with ${this.campaign.campaign_type} campaign type. 
            Content for Prime is only compatible with Contextual Message, Multi Page Contextual Message, eCommerce, and Map Dirty campaign types.`,
          displayOkButton: true
        }
      });
      return;
    }

    this.dialogService
      .open(CampaignStatusHandlerComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: ACTIONS.ACTIVATE,
          campaign: Campaign.copy(this.campaign) as Campaign
        }
      })
      .onClose.subscribe(() => {
        this.close();
      });
  }

  public deactivateCampaign() {
    this.dialogService
      .open(CampaignStatusHandlerComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: ACTIONS.DEACTIVATE,
          campaign: Campaign.copy(this.campaign) as Campaign
        }
      })
      .onClose.subscribe(() => {
        this.close();
      });
  }

  public promoteCampaign() {
    if (this.valid_campaign) {
      const content = this.contents.find((element) => element.content_id === this.campaign.content_id);
      this.dialogService.open(CampaignPromotionComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          campaign: Campaign.copy(this.campaign) as Campaign,
          insights: this.insights,
          content,
          username: this.username
        }
      });
    } else {
      this.dialogService.open(NgxGenericModalComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          title: $localize`Invalid Campaign Configuration`,
          message: $localize`Invalid Configuration: The content attached to this campaign is set up for the Prime App and is not compatible with ${this.campaign.campaign_type} campaign type. 
            Content for Prime is only compatible with Contextual Message, Multi Page Contextual Message, eCommerce, and Map Dirty campaign types.`,
          displayOkButton: true
        }
      });
    }
  }

  public close() {
    this.ref.close();
  }

  private deserializeCondition() {
    if (this.campaign.condition?.AND) {
      this.campaign.condition.AND.forEach((item) => {
        if (item.campaign_id) {
          this.condition_all.push(item);
          this.time_frame = item.time_frame;
        } else if (item.NOT) {
          this.condition_not_all.push(item.NOT);
          this.time_frame = item.NOT.time_frame;
        } else if (item.OR) {
          item.OR.forEach((item) => {
            if (item.campaign_id) {
              this.condition_any.push(item);
              this.time_frame = item.time_frame;
            } else if (item.NOT) {
              this.condition_not_any.push(item.NOT);
              this.time_frame = item.NOT.time_frame;
            }
          });
        }
      });
      this.show_condition = true;
    }
  }

  public getTooltip(campaignType: string) {
    return this.campaignTypes.find((t) => t.value === campaignType)?.tooltip;
  }
}
